<!--
  RowsEditor — a value relation's rows (times, contacts) as a list you edit in
  place. Its state is a useRowsEditor; what a row looks like and how it's
  edited come from the slots.

  One line is open at a time. Click a line to open it (the `open` slot: the
  line becomes its row's editor) and click its header again, or another line,
  to close it. An edited row keeps its id; × removes a row (Undo brings it
  back). The add line is last (the `add` slot), opened the same way — click
  into its box or start typing — and Enter adds its row and starts another.
  Enter on an empty add line is Done; text that isn't a row yet stays to be
  fixed.

  Nothing leaves until Done, which emits `done` with the handover delta.
  Cancel emits `cancel`; the host drops the edit, as it does on a click away.

  Slots:
    - line { row }: a closed line's content.
    - badges { row }: badges beside it, before new / edited.
    - open { row, close, remove, edited }: the open line's editor.
    - add { open, enter, toggle, autofocus }: the add line's editor.

  A row editor in `open` / `add` follows one shape, styled here: `.head` (a
  `.kind-icon`, a `.typed` box, then its end) and, open, `.fields` under it (a
  `.hint`, rows of `.inputs`).
-->
<template>
  <div class="rows-editor">
    <template v-for="row in editor.ordered.value" :key="row.key">
      <div v-if="editor.open.value === row.key" class="line is-open">
        <slot
          name="open"
          :row="row"
          :close="close"
          :remove="() => editor.remove(row)"
          :edited="editor.isEdited(row)"
        />
      </div>

      <div v-else class="line" :class="{ 'is-removed': row.removed }">
        <button
          type="button"
          class="open"
          :disabled="row.removed"
          @click="editor.openLine(row.key)"
        >
          <slot name="line" :row="row" />
        </button>
        <slot name="badges" :row="row" />
        <span v-if="editor.isNew(row)" class="badge is-new">new</span>
        <span v-else-if="editor.isEdited(row)" class="badge is-edited">edited</span>
        <button v-if="row.removed" type="button" class="undo" @click="editor.restore(row)">
          Undo
        </button>
        <button
          v-else
          type="button"
          class="remove"
          :aria-label="`Remove this ${noun}`"
          @click="editor.remove(row)"
        >
          <font-awesome-icon icon="xmark" />
        </button>
      </div>
    </template>

    <div
      v-if="editor.canAdd.value"
      ref="addLine"
      class="line adding"
      :class="{ 'is-open': editor.open.value === ADD }"
      @pointerdown="onAddPointer"
      @input="editor.openLine(ADD)"
    >
      <slot
        name="add"
        :open="editor.open.value === ADD"
        :enter="onAddEnter"
        :toggle="toggleAdd"
        :autofocus="autofocus && editor.addCount.value === 0"
      />
    </div>

    <div v-if="!footerWhenDirty || editor.dirty.value" class="actions">
      <span class="summary">{{ editor.summary.value }}</span>
      <div class="buttons">
        <button type="button" class="btn btn-sm" @click="emit('cancel')">Cancel</button>
        <button type="button" class="btn btn-sm btn-primary" @click="finish">Done</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="R extends { id?: number | string | null }">
import { nextTick, ref } from "vue"
import { ADD, type RowsDelta, type RowsEditor } from "@/composables/useRowsEditor"

const props = withDefaults(
  defineProps<{
    editor: RowsEditor<R>
    /** What a row is, for labels: "time", "contact". */
    noun?: string
    /** Focus the add line on open — an overlay does, a form on a page doesn't. */
    autofocus?: boolean
    /** Show Cancel / Done only once something changed (a form at rest). */
    footerWhenDirty?: boolean
  }>(),
  { noun: "row", autofocus: true, footerWhenDirty: false }
)

const emit = defineEmits<{
  done: [delta: RowsDelta]
  cancel: []
}>()

function close() {
  props.editor.openLine(null)
}

// --- The add line ---------------------------------------------------------------

const addLine = ref<HTMLElement | null>(null)
const addBox = () => addLine.value?.querySelector<HTMLInputElement>(".typed")

/** Clicking into the add line's box opens it; focus put there for typing
 *  leaves it closed until something is typed. */
function onAddPointer(event: PointerEvent) {
  if ((event.target as HTMLElement).matches(".typed")) props.editor.openLine(ADD)
}

function toggleAdd() {
  props.editor.toggleLine(ADD)
}

/** Enter on the add line adds its row and starts another. On an empty line
 *  it's Done; text that isn't a row yet stays to be fixed. */
function onAddEnter() {
  if (props.editor.addPending()) nextTick(() => addBox()?.focus())
  else if (!addBox()?.value.trim()) finish()
}

function finish() {
  emit("done", props.editor.handover.value)
}
</script>

<style>
.rows-editor {
  display: flex;
  flex-direction: column;
  min-width: 0;

  /* A closed line: the row, its badges, then × / Undo. */
  .line {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 28px;
    padding: 0 2px 0 1px;
    border-bottom: 1px solid var(--auger-border-light);

    &.is-removed .open {
      text-decoration: line-through;
      color: var(--auger-text-light);
    }
  }

  /* An open line, and the add line: the row's editor, its line on top and,
     open, its inputs in a panel under it. The line keeps its place and
     background. */
  .line.is-open,
  .line.adding {
    flex-direction: column;
    align-items: stretch;
    gap: 0;

    > * {
      min-width: 0;
    }
  }

  .line.is-open .fields {
    margin: 0 -2px 0 -1px;
    padding: 8px 8px 10px calc(1.25em + 0.4em + 1px);
    border-top: 1px solid var(--auger-border-light);
    background: var(--auger-bg-subtle);
  }

  .line.adding {
    border-bottom: none;
  }

  .open {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  /* A row editor's line: icon, the typed box, then whatever closes it. The
     icon and text sit as far apart as on a closed line. */
  .head {
    display: flex;
    align-items: center;
    gap: 0.4em;
    min-height: 28px;
    cursor: pointer;
  }

  /* The size a closed line's icon is, so opening a line doesn't grow it. */
  .kind-icon,
  .plus {
    flex: none;
    font-size: 0.9em;
  }

  .kind-icon {
    color: var(--auger-text-weak);
  }

  .typed {
    flex: 1;
    min-width: 0;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    color: inherit;

    &.is-invalid {
      color: var(--auger-danger);
    }

    &.is-static {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .hint {
    font-size: var(--auger-size-7);
    color: var(--auger-text-weak);

    &.is-error {
      color: var(--auger-danger);
    }
  }

  .inputs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;

    &:empty {
      display: none;
    }
  }

  /* One segmented control: the kinds share borders, the chosen one filled. */
  .kinds {
    display: flex;
    align-self: flex-start;
    max-width: 100%;
    border: 1px solid var(--auger-border);
    border-radius: var(--auger-radius);
    overflow: hidden;
  }

  .kind {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 7px;
    border: none;
    border-left: 1px solid var(--auger-border);
    background: var(--auger-bg);
    color: var(--auger-text);
    font: inherit;
    white-space: nowrap;
    cursor: pointer;

    &:first-child {
      border-left: none;
    }

    &:hover:not(.is-selected) {
      background: var(--auger-bg-muted);
    }

    &.is-selected {
      background: var(--auger-text-strong);
      color: var(--auger-text-inverted);
    }
  }

  .check {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .muted,
  .was {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--auger-text-weak);
  }

  .was {
    text-decoration: line-through;
  }

  .badge {
    flex: none;
    padding: 1px 6px;
    border-radius: var(--auger-radius);
    font-size: 0.9em;
    font-weight: 600;

    &.is-new {
      background: var(--auger-success-light);
      color: var(--auger-text-strong);
    }

    &.is-edited {
      background: var(--auger-link-light);
      color: var(--auger-link-dark);
    }

    &.is-warning {
      background: var(--auger-warning-light);
      color: var(--auger-warning-dark);
    }
  }

  .remove,
  .undo {
    flex: none;
    border: none;
    border-radius: 4px;
    background: none;
    font: inherit;
    cursor: pointer;
  }

  .remove {
    width: 22px;
    height: 22px;
    color: var(--auger-text-light);

    &:hover {
      color: var(--auger-danger);
      background: var(--auger-danger-light);
    }
  }

  .undo {
    color: var(--auger-link);
  }

  .plus {
    color: var(--auger-primary);
  }

  .enter,
  .summary {
    color: var(--auger-text-weak);
  }

  .enter {
    flex: none;
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 2px 0 1px;
    border-top: 1px solid var(--auger-border-light);

    .buttons {
      gap: 6px;
      margin: 0;
    }

    .btn {
      margin: 0;
    }
  }
}

/* A grid overlay strips its inputs' chrome so a cell reads as the cell going
   editable; an open row's several inputs need their boxes back to read apart. */
.grid-editor .rows-editor .fields :is(.input, select) {
  padding: 0 6px;
  border: 1px solid var(--auger-border);
  border-radius: var(--auger-radius);
  background: var(--auger-bg);
}
</style>
